import _ from 'lodash'
import moment from 'moment'

import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import { employeeUser } from 'modules/core/store/collections/users'

import { employeeHireDate, employeeJobTitle } from 'modules/contracts/lib/utils'
import contractVersions from 'modules/contracts/store/collections/contractVersions'
import type Employee from 'modules/employees/store/models/Employee'
import type { Locations } from 'modules/locations/store/collections/locations'
import memberships from 'modules/teams/store/collections/memberships'
import { Teams } from 'modules/teams/store/collections/teams'
import { employeeTeams } from 'modules/teams/store/employeeTeams'
import type { TimeoffPolicies } from 'modules/timeoff/store/collections/timeoffPolicies/policies'

type GroupFilters = {
  id: number
  name: string
  count: number
  employees: Employee[]
}

const locationFilter = (employees: Employee[], locations: Locations): GroupFilters[] => {
  const employeesByLocation: Record<string, Employee[]> = {}

  employees.forEach((employee) => {
    const locationId = employee.get('location_id')
    if (!employeesByLocation[locationId]) {
      employeesByLocation[locationId] = []
    }
    employeesByLocation[locationId]?.push(employee)
  })

  return locations.map((location) => {
    const employeesForLocation = employeesByLocation[location.id] || []
    return {
      id: location.id,
      name: location.attributes.name,
      count: employeesForLocation.length,
      employees: employeesForLocation,
    }
  })
}

const managerFilter = (employees: Employee[], totalEmployees: Employee[]) => {
  const groupedEmployees = _.groupBy(employees, (e) => e.attributes.manager_id)

  return Object.entries(groupedEmployees).map(([key, value]) => {
    const managerID = parseInt(key)
    const manager = totalEmployees.find((e) => {
      return e.attributes.id === managerID
    })

    const managerName = managerID > 0 && manager ? employeeUser(manager)?.fullName() : 'No manager' // TODO: I18n

    return {
      id: Number(key),
      name: managerName,
      count: value.length,
      employees: value,
    }
  })
}

const teamsFilter = (employees: Employee[], teams: Teams) => {
  return teams.map((team) => {
    const employeesFiltered = employees.filter((e) => {
      const teamsIds = employeeTeams(teams, memberships, e.id).map((t) => t.attributes.id)
      return _.includes(teamsIds, team.id)
    })

    return {
      id: team.id,
      name: team.attributes.name,
      employees: employeesFiltered,
      count: employeesFiltered.length,
    }
  })
}

const timeoffManagerFilter = (employees: Employee[], totalEmployees: Employee[]) => {
  const groupedEmployeesByTimeOffManager = _.groupBy(
    employees,
    (e) => e.attributes.timeoff_manager_id
  )

  return Object.entries(groupedEmployeesByTimeOffManager).map(([key, value]) => {
    const managerID = parseInt(key)
    const manager = totalEmployees.find((employee) => {
      return employee.attributes.id === managerID
    })

    const managerName =
      managerID > 0 && manager ? employeeUser(manager)?.fullName() : 'No supervisor' // TODO: I18n

    return {
      id: Number(key),
      name: managerName,
      count: value.length,
      employees: value,
    }
  })
}

const timeoffPolicyFilter = (employees: Employee[], policies: TimeoffPolicies) => {
  return policies.map((policy) => {
    const employeesFilteredByTimeOffPolicy = employees.filter((e) => {
      return e.attributes.timeoff_policy_id === policy.id
    })

    return {
      id: policy.id,
      name: policy.attributes.name,
      employees: employeesFilteredByTimeOffPolicy,
      count: employeesFilteredByTimeOffPolicy.length,
    }
  })
}

const jobTitleFilter = (employees: Employee[]) =>
  genericFilter(employees, (e) => employeeJobTitle(contractVersions, e))

const hiredOnFilter = (employees: Employee[]) =>
  genericFilter(employees, (e) => moment(employeeHireDate(contractVersions, e)).format('MMMM YYYY'))

const hiredYearFilter = (employees: Employee[]) =>
  genericFilter(employees, (e) => moment(employeeHireDate(contractVersions, e)).format('YYYY'))

const genderFilter = (employees: Employee[]) =>
  genericFilter(
    employees,
    (e) => e.has('gender') && e.attributes.gender,
    (name) => (name === 'false' ? 'No group' : name)
  )

const nationalityFilter = (employees: Employee[]) =>
  genericFilter(
    employees,
    (e) => e.has('nationality') && e.attributes.nationality,
    (name) => (name === 'false' ? 'No group' : name)
  )

const cityFilter = (employees: Employee[]) =>
  genericFilter(
    employees,
    (e) => e.has('city') && e.attributes.city,
    (name) => (name === 'false' ? 'No group' : name)
  )

const countryFilter = (employees: Employee[]) =>
  genericFilter(
    employees,
    (e) => e.has('country') && e.attributes.country,
    (name) => (name === 'false' ? 'No group' : name)
  )

const legalEntityFilter = (employees: Employee[]) =>
  genericFilter(employees, (e) => employeeLegalEntity(e).attributes.legal_name)

const genericFilter = (
  employees: Employee[],
  iterator: (e: Employee) => unknown,
  rename: (name: string) => string = (name) => name
) => {
  return Object.entries(_.groupBy(employees, iterator)).map(([key, value], index) => {
    return {
      id: index,
      name: rename(key),
      count: value.length,
      employees: value,
    }
  })
}

const useGroupFilter = (
  groupFilter: string,
  employees: Employee[],
  locations: Locations,
  teams: Teams,
  policies: TimeoffPolicies,
  totalEmployees: Employee[],
  groupOrder: 'asc' | 'desc'
): GroupFilters[] => {
  const filters: Record<string, () => GroupFilters[]> = {
    location_id: () => locationFilter(employees, locations),
    manager_id: () => managerFilter(employees, totalEmployees),
    team_id: () => teamsFilter(employees, teams),
    timeoff_manager_id: () => timeoffManagerFilter(employees, totalEmployees),
    timeoff_policy_id: () => timeoffPolicyFilter(employees, policies),
    job_title: () => jobTitleFilter(employees),
    hired_on: () => hiredOnFilter(employees),
    hired_year: () => hiredYearFilter(employees),
    gender: () => genderFilter(employees),
    nationality: () => nationalityFilter(employees),
    city: () => cityFilter(employees),
    country: () => countryFilter(employees),
    legal_entity: () => legalEntityFilter(employees),
  }

  const filter = filters[groupFilter]

  const groups = filter ? filter() : []

  return _.orderBy(groups, 'name', [groupOrder])
}

export default useGroupFilter
